/**
 Copyright (c) 2021 Oracle and/or its affiliates.
 The Universal Permissive License (UPL), Version 1.0
 */

'use strict';

define('vb/components/oj-vb-fragment-slot/fragmentSlotViewModel',['knockout',
  'vb/private/log',
  'vb/private/utils',
  'vb/private/constants',
  'ojs/ojhtmlutils',
], (ko, Log, Utils, Constants, HtmlUtils) => {
  /** @typedef {import('ojs/ojcomposite').PropertyChangedContext} PropertyChangedContext */
  const logger = Log.getLogger('/vb/components/oj-vb-fragment-slot');
  const createComponentBridge = (factory, props) => factory.create(Constants.ComponentBridgeType.FRAGMENT_SLOT, props);

  /**
   * ViewModel class for the oj-vb-fragment-slot CCA
   * @category Component
   */
  class FragmentSlotViewModel {
    constructor(context) {
      this.log = logger;
      const props = context.properties;
      this.composite = context.element;
      this.name = props.name;
      if (!props.name) {
        const errMsg = 'The fragment-slot \'name\' was not provided. This is required in order to identify the slot '
        + 'content. Check the configuration of the oj-vb-fragment-slot component!';
        throw new Error(errMsg);
      }
      // the bridge property is required to be set
      if (props.bridge) {
        // if a 'bridge' property is configured on the oj-vb-fragment-slot then that is our hook into VB
        // (fragments in VDOM components will need to configure this property and we also prefer authors move to
        // always setting this property on all fragment CCAs)
        this.bridge = createComponentBridge(props.bridge, props);
      } else {
        const errMsg = 'the \'bridge\' property was not configured on the oj-vb-fragment-slot component with'
          + `${this.name}. For best results it is recommended that the 'bridge' property be set to "[[ vbBridge ]]".`;
        throw new Error(errMsg);
      }
      this.context = props.context || {};
      // a default template that is used for each slot can also be defined by fragment author
      this.defaultTemplates = this.composite.querySelectorAll('template');
    }

    /**
     * Composite lifecycle callback
     * Invoked after the View is first inserted into the DOM and then each time the composite is reconnected to the
     * DOM after being disconnected. Note that if the composite needs to add/remove event listeners, we recommend
     * using this and the disconnected methods.
     * @param context
     */
    connected(context) {
      this.log.info('oj-vb-fragment-slot component', this.name, 'connected to DOM');
      this.bridge.connected(context, this.name);
    }

    /**
     * Composite lifecycle callback called when the composite component is disconnected from the DOM.
     * Invoked when this composite component is disconnected from the DOM.
     * @param element
     */
    disconnected(element) {
      // perform cleanup here of fragment-slot default content when DOM is disconnected.
      this.log.info('oj-vb-fragment-slot', this.name, 'disconnected from DOM');
      const domNodes = element ? element.children : undefined;
      this.bridge.disconnected(domNodes, this.name);
    }

    /**
     * Gets the fragment-slot's config object. The view and data are likely to be Promises that are fully
     * resolved when the view is retrieved  and model fully initialized/activated.
     * @return a Promise that resolves with an object that represents the oj.ojBindDom.Config object for the slot
     * - contains the resolved values for view and data.
     * @see BindDom
     */
    getSlotConfig() {
      return this.bridge.getSlotConfig(this.name, this.defaultTemplates).then((config) => {
        // don't create a ko dependency!
        const viewPromise = (config && config.view) || Promise.resolve();
        const dataPromise = (config && config.data) || Promise.resolve();
        const aliasPromise = (config && config.alias) || Promise.resolve();

        return Promise.all([viewPromise, dataPromise, aliasPromise]).then(([viewHTMLStr = '', data, alias = '']) => {
          // view: the Nodes to be inserted. Note that oj-bind-dom does not clone the node array before applying
          // bindings to it. If the application needs to have access to the original node array, it should set the
          // 'view' property to a cloned copy. Node arrays should not have a longer lifespan than their oj-bind-dom
          // element as would be the case for a node array created in the application model and referenced by an
          // oj-bind-dom element that is detached and reattached by another binding element or script.
          const viewNodeArray = HtmlUtils.stringToNodeArray(viewHTMLStr);
          let slotData;
          if (this.context) { // default value is an object
            // if an alias was provided then generally it means a $current was setup by CCA for the template slot
            // content.
            if (alias && !this.context[alias]) {
              // assign new alias to $current
              this.context[alias] = this.context.$current || {};
            }
            slotData = Object.assign({}, data, this.context);
          }
          // include other props in config besides the obvious
          return Object.assign(config, { view: viewNodeArray, data: slotData });
        });
      });
    }

    /**
     * Composite ViewModel callback called when a property on the composite is updated and before the
     * [property]Changed event is fired.
     * @param {PropertyChangedContext} context
     */
    propertyChanged(context) { // eslint-disable-line class-methods-use-this
      if (context.property === 'name') {
        // writeback to (fragment) name is currently not supported. writeback is set to false by default but
        //  we still want to log error if author changes name on the fly
        const { previousValue, value } = context;
        if (value !== this.name) {
          this.log.error('The oj-vb-fragment-slot \'locator\' property was changed from', previousValue, 'to',
            value, '. The locator cannot be changed dynamically.');
        }
      }
    }
  }

  return FragmentSlotViewModel;
});

